import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Scissors, Heart, MoveRight, Bot, ExternalLink, Share, X, Zap, Sparkles } from "lucide-react";
import { FaAmazon } from "react-icons/fa";
import ActionButtons from "@/components/ActionButtons";
import AiTryOn from "@/components/AiTryOn";
import BookingChat from "@/components/BookingChat";
import QuickBook from "@/components/QuickBook";
import { LINKS } from "@/constants/links";
import useFeatures from "@/constants/useFeatures";
import { isTenant, tenantSlug } from "@/tenant";

const HERO = "/hero-salon.jpg";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MARQUEE = [
  "Barber Latin",
  "Prenota",
  "Taglio",
  "Barba",
  "Grooming",
  "Prodotti Amazon",
];

const lineReveal = {
  hidden: { y: "115%" },
  show: (d) => ({
    y: 0,
    transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function HomePage({ goTo }) {
  const [showChoice, setShowChoice] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelName, setCancelName] = useState("");
  const [cancelPhone, setCancelPhone] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  const [showTryOn, setShowTryOn] = useState(false);
  const features = useFeatures();
  const soloEnabled = features.sumup;
  const [tenantInfo, setTenantInfo] = useState(null);

  useEffect(() => {
    if (isTenant()) {
      fetch(`${BACKEND}/api/tenant-info?slug=${tenantSlug()}`)
        .then((r) => (r.ok ? r.json() : { salon_name: "", active: false }))
        .then(setTenantInfo)
        .catch(() => setTenantInfo({ salon_name: "", active: false }));
    }
  }, []);

  const brandName = isTenant() ? tenantInfo?.salon_name || "" : null;

  const submitCancel = async () => {
    if (cancelBusy || cancelName.trim().length < 2 || cancelPhone.trim().length < 8) return;
    setCancelBusy(true);
    setCancelResult(null);
    try {
      const res = await fetch(`${BACKEND}/api/booking/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: cancelName.trim(), telefono: cancelPhone.trim() }),
      });
      const d = await res.json();
      if (res.ok) {
        setCancelResult({
          ok: true,
          msg: `Prenotazione annullata ✅ — ${d.booking.giorno} alle ${d.booking.ora}`,
        });
      } else {
        setCancelResult({ ok: false, msg: d.detail || "Errore, riprova" });
      }
    } catch {
      setCancelResult({ ok: false, msg: "Errore di connessione, riprova" });
    }
    setCancelBusy(false);
  };

  const [iosHint, setIosHint] = useState(() => {
    if (typeof navigator === "undefined") return false;
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const standalone =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;
    return ios && !standalone && localStorage.getItem("bl_ios_hint") !== "1";
  });
  if (isTenant() && tenantInfo && !tenantInfo.active) {
    return (
      <div
        data-testid="tenant-suspended"
        className="flex h-full min-h-[70dvh] flex-col items-center justify-center gap-5 bg-zinc-950 px-8 text-center"
      >
        <Scissors size={40} className="text-zinc-700" />
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Periodo di prova terminato</h1>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            I 3 giorni gratuiti sono finiti. Per continuare a usare l&apos;app, attiva il tuo piano:
          </p>
        </div>
        <div className="flex w-full max-w-xs flex-col gap-3">
          <a
            data-testid="suspended-pay"
            href={LINKS.pay}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl bg-amber-400 py-3.5 text-xs font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300"
          >
            Paga con SumUp
          </a>
          <a
            data-testid="suspended-whatsapp"
            href={`${LINKS.whatsapp}?text=${encodeURIComponent("Ciao! Voglio attivare la mia app da barbiere")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl bg-emerald-500 py-3.5 text-xs font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
          >
            Scrivici su WhatsApp
          </a>
        </div>
        <p className="text-[11px] text-zinc-600">
          Dopo il pagamento l&apos;app si riattiva subito.
        </p>
      </div>
    );
  }


  return (
    <div data-testid="page-home" className="flex flex-col gap-8">
      <header className="relative -mx-6 -mt-6 h-72 overflow-hidden">
        <motion.img
          src={HERO}
          alt="Interno del salone Barber Latin"
          className="h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-zinc-950/55" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-zinc-950 [mask-image:linear-gradient(to_top,black,transparent)]" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-5">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-300">
              <Scissors size={12} /> Barbiere &amp; Grooming
            </span>
            <span
              data-testid="amazon-badge"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-zinc-950/70 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur"
            >
              <FaAmazon size={13} className="text-amber-400" /> Affiliato Amazon
            </span>
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.05] text-white">
            <span className="block overflow-hidden pb-0.5">
              <motion.span
                className="block"
                variants={lineReveal}
                initial="hidden"
                animate="show"
                custom={0.1}
              >
                {brandName || "Barber Latin"}
              </motion.span>
            </span>
            {!brandName && (
              <span className="block overflow-hidden pb-1 pl-16">
                <motion.span
                  className="font-signature -mt-2 block text-3xl font-medium text-amber-400"
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  custom={0.28}
                >
                  Jordan
                </motion.span>
              </span>
            )}
          </h1>
        </div>
      </header>

      <div
        data-testid="marquee"
        className="relative -mx-6 overflow-hidden border-y border-white/10 py-3"
      >
        <div className="animate-marquee flex w-max items-center gap-8 whitespace-nowrap">
          {[...MARQUEE, ...MARQUEE].map((t, i) => (
            <span
              key={i}
              className="flex items-center gap-8 text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500"
            >
              {t} <Scissors size={11} className="text-amber-400/70" />
            </span>
          ))}
        </div>
      </div>

      {isTenant() && tenantInfo?.trial && tenantInfo.days_left != null && (
        <div
          data-testid="trial-banner"
          className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-center"
        >
          <p className="text-xs font-bold text-amber-300">
            Prova gratuita: {tenantInfo.days_left}{" "}
            {tenantInfo.days_left === 1 ? "giorno rimasto" : "giorni rimasti"}
          </p>
          <p className="mt-0.5 text-[10px] text-zinc-500">
            Poi l&apos;app si blocca fino all&apos;attivazione del piano.
          </p>
        </div>
      )}

      <section className="flex flex-col gap-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-600">
          01 — Le tue azioni
        </span>
        <h2 className="text-base font-bold text-white md:text-lg">Tutto a portata di tap</h2>
        <p className="-mt-1 text-sm text-zinc-400">
          Prenota con l&apos;assistente, acquista i prodotti che uso in salone o scrivimi direttamente.
        </p>
        <ActionButtons onBooking={() => setShowChoice(true)} />
        {features.ai_photo && (
          <button
            data-testid="ai-tryon-open"
            onClick={() => setShowTryOn(true)}
            className="mt-3 flex w-full items-center gap-4 rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-400/10 to-transparent px-5 py-4 text-left transition-[border-color,transform] duration-200 hover:border-amber-400/70 active:scale-[0.98]"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-amber-400">
              <Sparkles size={22} />
            </span>
            <span className="flex-1">
              <span className="block text-base font-extrabold leading-tight text-white">
                Prova il tuo taglio con l&apos;IA ✨
              </span>
              <span className="block text-xs font-medium text-zinc-400">
                Fatti un selfie e vedi come stai con il taglio che scegli
              </span>
            </span>
          </button>
        )}
      </section>

      {iosHint && (
        <div
          data-testid="ios-install-hint"
          className="flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-amber-400">
            <Share size={16} />
          </span>
          <p className="flex-1 text-xs leading-relaxed text-zinc-300">
            <span className="font-bold text-white">Installa l&apos;app sul tuo iPhone:</span> tocca il
            pulsante <span className="font-bold text-amber-300">Condividi</span> di Safari (il
            quadrato con la freccia in basso) e poi scegli{" "}
            <span className="font-bold text-amber-300">&quot;Aggiungi a Home&quot;</span>
          </p>
          <button
            data-testid="ios-hint-close"
            onClick={() => {
              setIosHint(false);
              localStorage.setItem("bl_ios_hint", "1");
            }}
            className="shrink-0 text-zinc-500 transition-colors duration-200 hover:text-white"
          >
            <X size={15} />
          </button>
        </div>
      )}

      <section
        data-testid="thanks-banner"
        className="flex items-start gap-3 rounded-2xl border border-white/10 bg-zinc-900 p-5"
      >
        <Heart size={18} className="mt-0.5 shrink-0 text-amber-400" fill="currentColor" />
        <p className="text-sm leading-relaxed text-zinc-300">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-600">
            02 — Grazie
          </span>
          Grazie di cuore per il tuo supporto: ogni acquisto tramite il link affiliato aiuta il
          salone, <span className="font-semibold text-white">senza costi aggiuntivi per te</span>.
        </p>
      </section>

      <button
        data-testid="swipe-hint"
        onClick={() => goTo(1)}
        className="mx-auto -mt-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-zinc-500 transition-colors duration-200 hover:text-amber-400"
      >
        Scorri per vedere i prodotti <MoveRight size={14} />
      </button>

      <AnimatePresence>
        {showChoice && (
          <motion.div
            data-testid="booking-choice-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowChoice(false)}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-zinc-950/70 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 90 }}
              animate={{ y: 0 }}
              exit={{ y: 90 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="mx-auto w-full max-w-md rounded-t-3xl border-t border-white/10 bg-zinc-900 p-6 pb-10"
            >
              <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                Prenota il tuo posto
              </p>
              <h3 className="mb-5 font-display text-xl font-bold text-white">Come preferisci?</h3>
              <div className="flex flex-col gap-3">
                {features.diretta && (
                  <button
                    data-testid="btn-choice-quick"
                    onClick={() => {
                      setShowChoice(false);
                      setShowQuick(true);
                    }}
                    className="flex items-center gap-4 rounded-2xl bg-emerald-500 px-5 py-4 text-left text-white shadow-[0_0_24px_rgba(16,185,129,0.25)] transition-[background-color,transform] duration-200 hover:bg-emerald-400 active:scale-[0.97]"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black/15">
                      <Zap size={22} />
                    </span>
                    <span className="flex-1">
                      <span className="block text-base font-extrabold leading-tight">
                        Prenota subito
                      </span>
                      <span className="block text-xs font-medium opacity-80">
                        Senza chat, in 1 minuto
                      </span>
                    </span>
                  </button>
                )}
                {features.chat && (
                <button
                  data-testid="btn-choice-ai"
                  onClick={() => {
                    setShowChoice(false);
                    setShowChat(true);
                  }}
                  className="flex items-center gap-4 rounded-2xl bg-emerald-500 px-5 py-4 text-left text-white shadow-[0_0_24px_rgba(16,185,129,0.25)] transition-[background-color,transform] duration-200 hover:bg-emerald-400 active:scale-[0.97]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black/15">
                    <Bot size={22} />
                  </span>
                  <span className="flex-1">
                    <span className="block text-base font-extrabold leading-tight">
                      Con Jordan, l&apos;assistente
                    </span>
                    <span className="block text-xs font-medium opacity-80">
                      Ti guido io, passo passo
                    </span>
                  </span>
                </button>
                )}
                {soloEnabled && (
                <button
                  data-testid="btn-choice-sumup"
                  onClick={() => window.open(LINKS.booking, "_blank", "noopener,noreferrer")}
                  className="flex items-center gap-4 rounded-2xl border border-white/15 bg-zinc-950 px-5 py-4 text-left transition-[border-color,transform] duration-200 hover:border-emerald-500/50 active:scale-[0.97]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                    <ExternalLink size={20} />
                  </span>
                  <span className="flex-1">
                    <span className="block text-base font-extrabold leading-tight text-white">
                      SumUp Bookings
                    </span>
                    <span className="block text-xs font-medium text-zinc-400">
                      Prenota dalla piattaforma SumUp
                    </span>
                  </span>
                </button>
                )}
                <button
                  data-testid="btn-choice-cancel"
                  onClick={() => {
                    setShowChoice(false);
                    setCancelResult(null);
                    setShowCancel(true);
                  }}
                  className="flex items-center gap-4 rounded-2xl border border-red-500/25 bg-zinc-950 px-5 py-4 text-left transition-[border-color,transform] duration-200 hover:border-red-500/50 active:scale-[0.97]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-400">
                    <X size={20} />
                  </span>
                  <span className="flex-1">
                    <span className="block text-base font-extrabold leading-tight text-white">
                      Annulla prenotazione
                    </span>
                    <span className="block text-xs font-medium text-zinc-400">
                      Cancella il tuo appuntamento con nome e numero
                    </span>
                  </span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCancel && (
          <motion.div
            data-testid="cancel-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCancel(false)}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-zinc-950/70 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 90 }}
              animate={{ y: 0 }}
              exit={{ y: 90 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="mx-auto w-full max-w-md rounded-t-3xl border-t border-white/10 bg-zinc-900 p-6 pb-10"
            >
              <div className="mb-1 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                  Annulla prenotazione
                </p>
                <button
                  data-testid="cancel-close-btn"
                  onClick={() => setShowCancel(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors hover:text-white"
                >
                  <X size={15} />
                </button>
              </div>
              <h3 className="mb-4 font-display text-xl font-bold text-white">
                Cancella il tuo appuntamento
              </h3>
              <p className="mb-4 text-xs leading-relaxed text-zinc-400">
                Scrivi il nome e il numero WhatsApp che hai usato per prenotare: annulliamo il
                tuo prossimo appuntamento e il barbiere riceve l&apos;avviso.
              </p>
              <div className="flex flex-col gap-3">
                <input
                  data-testid="cancel-name-input"
                  value={cancelName}
                  onChange={(e) => setCancelName(e.target.value)}
                  placeholder="Nome (es. Mario Rossi)"
                  className="h-11 rounded-xl border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-red-400/50"
                />
                <input
                  data-testid="cancel-phone-input"
                  value={cancelPhone}
                  onChange={(e) => setCancelPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitCancel()}
                  inputMode="tel"
                  placeholder="Numero WhatsApp (es. 3331234567)"
                  className="h-11 rounded-xl border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-red-400/50"
                />
                <button
                  data-testid="cancel-submit-btn"
                  onClick={submitCancel}
                  disabled={cancelBusy || cancelName.trim().length < 2 || cancelPhone.trim().length < 8}
                  className="h-11 rounded-xl bg-red-500 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-red-400 disabled:opacity-40"
                >
                  {cancelBusy ? "Annullamento…" : "Annulla prenotazione"}
                </button>
                {cancelResult && (
                  <p
                    data-testid={cancelResult.ok ? "cancel-success" : "cancel-error"}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold ${
                      cancelResult.ok
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                        : "border-red-500/30 bg-red-500/10 text-red-300"
                    }`}
                  >
                    {cancelResult.msg}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showChat && <BookingChat onClose={() => setShowChat(false)} />}
        {showQuick && <QuickBook onClose={() => setShowQuick(false)} />}
        {showTryOn && <AiTryOn onClose={() => setShowTryOn(false)} />}
      </AnimatePresence>
    </div>
  );
}
